import styled, { css } from "styled-components";
import { borderRadius, fontWeights, spacing, typographyMixin } from "@magicbi/ui-library";
import type { DateStripTone } from "./DateStrip.types";

export const Strip = styled.ol`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(5.5rem, 1fr));
  gap: ${spacing[2]};
  margin: 0;
  padding: 0;
  list-style: none;
`;

const TONE: Record<DateStripTone, ReturnType<typeof css> | null> = {
  default: null,
  first: css`
    border-color: ${({ theme }) => theme.colors.interactive.primary};
    background: ${({ theme }) => theme.colors.background.tertiary};
    & > [data-part="kicker"],
    & > [data-part="note"] {
      color: ${({ theme }) => theme.colors.text.link};
      font-weight: ${fontWeights.semibold};
    }
  `,
  changed: css`
    border-style: dashed;
    border-color: ${({ theme }) => theme.colors.warning[500]};
    & > [data-part="note"] {
      color: ${({ theme }) => theme.colors.warning.text};
      font-weight: ${fontWeights.semibold};
    }
  `,
  more: css`
    border-style: dashed;
    border-color: ${({ theme }) => theme.colors.border.default};
    background: ${({ theme }) => theme.colors.background.secondary};
    & > [data-part="big"] {
      ${typographyMixin("heading", "h4")}
      color: ${({ theme }) => theme.colors.text.secondary};
    }
  `,
};

export const Tile = styled.li<{ $tone: DateStripTone }>`
  display: grid;
  justify-items: center;
  align-content: start;
  min-width: 0;
  padding: ${spacing[2]} ${spacing[1]} ${spacing[2]};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  border-radius: ${borderRadius.xl};
  background: ${({ theme }) => theme.colors.background.primary};
  text-align: center;
  ${({ $tone }) => TONE[$tone]}
`;

export const Kicker = styled.span`
  ${typographyMixin("caption", 2)}
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const Big = styled.span`
  display: grid;
  place-items: center;
  min-height: 1.875rem;
  ${typographyMixin("heading", "h2")}
  font-weight: ${fontWeights.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  & > svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

export const Caption = styled.span`
  ${typographyMixin("caption", 2)}
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const Note = styled.span`
  margin-top: ${spacing[1]};
  ${typographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;
